// Use http://pxtoem.com/ for px to em measurements...

import { DEFAULT_ASSETS_BASE_PATH, DOMAIN_BASE_URL } from './config';

const mobileColumnWidth = 8; // this will be rem, so 80px on 10px base
const mobileColumnGap = 1.6; // this will be rem, so 16px on 10px base
const mobileGridWidth = 4 * mobileColumnWidth + 3 * mobileColumnGap; // this will be rem, so 368px on 10px base
const mobileDesignWidth = 4 * mobileColumnWidth + 5 * mobileColumnGap; // this will be rem, so 400px on 10px base
const mobileImageWidth = 3 * mobileColumnWidth + 2 * mobileColumnGap; // this will be rem, and covers 2/3 of the mobile viewport
const mobileImageContentWrapperWidth =
  6 * mobileColumnWidth + 5 * mobileColumnGap; // rem, and extends over the viewport to indicate scrollable content

const standardColumnWidth = 8; // this will be rem, so 80px on 10px base
const standardColumnGap = 1.8; // this will be rem, so 24px on 10px base
const twelveColumnGridWidth = 12 * standardColumnWidth + 9 * standardColumnGap; // this will be rem, so 1016px on 10 px base
const tenColumnDesignWidth = 10 * standardColumnWidth + 11 * standardColumnGap; // this will be rem, so 1064px on 10 px base
const twelveColumnDesignWidth =
  12 * standardColumnWidth + 13 * standardColumnGap; // this will be rem, so 1272px on 10 px base
const standardDoubleImageWidth =
  6 * standardColumnWidth + 4 * standardColumnGap;

const contentNarrowWidth = 60;
const baseFontSizePx = 10;

const breakPointMinWidth = '320px';
const breakPointTenColumn = '480px';
const breakPointSmallTablet = '650px';
const breakPointTablet = '768px';
const breakPointSmallDesktop = '1000px';
const breakPointTwelveColumn = `${tenColumnDesignWidth * baseFontSizePx}px`; // 1064px
const breakPointFullWidth = `${twelveColumnDesignWidth * baseFontSizePx}px`; // 1272px

const standardTileWidth = '100%';
const standardTileHeight = 'auto';
const standardTileImageHeight = '13.3rem';
const standardTileCharacterLimit = 100;
const standardTileTitleCharacterLimit = 45;

const mastheadLargeHeight = '42.4rem';
const mastheadIndentHeight = '9.6rem';
const mastheadMobileIndentHeight = '5.5rem';

const vars = {
  baseDomain: DOMAIN_BASE_URL,
  /* --- FONTAGE --- */
  primaryFontFamily: "'Inter', sans-serif;",

  // New font size implementation:
  fontSize1: `font-size: 1rem;
  @media screen and (min-width: ${breakPointTablet}) {
    font-size: 1.2rem;
  }
  @media screen and (min-width: ${breakPointTwelveColumn}) {
    font-size: 1.2rem;
  }`,

  fontSize2: `font-size: 1.2rem;
  @media screen and (min-width: ${breakPointTablet}) {
    font-size: 1.4rem;
  }
  @media screen and (min-width: ${breakPointTwelveColumn}) {
    font-size: 1.4rem;
  }`,

  fontSize3: `font-size: 1.3rem;
  @media screen and (min-width: ${breakPointTablet}) {
    font-size: 1.5rem;
  }
  @media screen and (min-width: ${breakPointTwelveColumn}) {
    font-size: 1.5rem;
  }`,

  fontSize4: `font-size: 1.4rem;
  @media screen and (min-width: ${breakPointTablet}) {
    font-size: 1.6rem;
  }
  @media screen and (min-width: ${breakPointTwelveColumn}) {
    font-size: 1.6rem;
  }`,

  fontSize5: `font-size: 1.6rem;
  @media screen and (min-width: ${breakPointTablet}) {
    font-size: 1.8rem;
  }
  @media screen and (min-width: ${breakPointTwelveColumn}) {
    font-size: 1.8rem;
  }`,

  fontSize6: `font-size: 1.6rem;
  @media screen and (min-width: ${breakPointTablet}) {
    font-size: 2rem;
  }
  @media screen and (min-width: ${breakPointTwelveColumn}) {
    font-size: 2rem;
  }`,

  fontSize7: `font-size: 1.8rem;
  @media screen and (min-width: ${breakPointTablet}) {
    font-size: 2.4rem;
  }
  @media screen and (min-width: ${breakPointTwelveColumn}) {
    font-size: 2.4rem;
  }`,

  fontSize8: `font-size: 2rem;
  @media screen and (min-width: ${breakPointTablet}) {
    font-size: 2.8rem;
  }
  @media screen and (min-width: ${breakPointTwelveColumn}) {
    font-size: 2.8rem;
  }`,

  fontSize9: `font-size: 2rem;
  @media screen and (min-width: ${breakPointTablet}) {
    font-size: 3.5rem;
  }
  @media screen and (min-width: ${breakPointTwelveColumn}) {
    font-size: 3.5rem;
  }`,

  fontSize10: `font-size: 2.2rem;
  @media screen and (min-width: ${breakPointTablet}) {
    font-size: 3.8rem;
  }
  @media screen and (min-width: ${breakPointTwelveColumn}) {
    font-size: 4.5rem;
  }`,

  /* --- FONT WEIGHTS --- */
  fontWeightLight: '200',
  fontWeightRegular: '400',
  fontWeightMedium: '500',
  fontWeightSemiBold: '600',
  fontWeightBold: '700',
  fontWeightExtraBold: '800',

  /* --- COLOURS --- */
  // general colours
  white: '#ffffff',
  black: '#000',
  modalTransparentGrey: 'rgba(47, 47, 70, 0.9)',
  deltaGreen: '#447e3a',
  deltaRed: '#ff0001',

  // topic colours
  coreDarkGrey: '#2f2f46',
  designStrongYellow: '#f7a70b',
  fashionPurple: '#482683',
  financePink: '#ba178c',
  foodOrange: '#f26722',
  plasticsBlue: '#0081c7',
  policyGrey: '#51608c',
  lightGrey: '#8f8f9b',
  learningGreen: '#00ae7d',
  citiesGreen: '#69be5a',
  builtEnvRed: '#e30613',

  transparentBorderGrey: '#E3E3E3',
  transparentCoreGrey: 'rgba(47, 47, 70, 0.45)',
  borderGrey: '#ccc',
  borderMidGrey: '#999',
  darkGrey: '#666',
  emfBlue: '#2F2F46',
  fadedTextGrey: '#9C9C9C',
  graphLightBlue: '#81ccf2',
  graphDarkBlue: '#3780c1',
  cardBorderGrey: '#D0D0E3',

  coreGreyGradient:
    'linear-gradient(rgba(47, 47, 70, 0), rgba(47, 47, 70, .8))',

  // fun colours :-) We'll organise these at some point!!
  orcBlue: '#E6E6F2',
  anduinBlue: '#6E6893',
  stingBlue: '#009FE3',
  gandalfGrey: '#EBEBEB',
  barrowWightGrey: '#F8F9FA',
  grimboldGrey: '#9D9D9D',
  gollumGrey: '#333333',
  greyHavens: '#F2F2F2',
  mordorGrey: '#2F2F46',
  gimlisBeard: '#F26722',
  underbellyOrange: '#FCE1D3',
  figmaGrey: '#E3E3E3',
  dunedainGrey: '#CCCCCC',

  /* --- LAYOUT MARGINS AND PADDINGS --- */
  spacingXs: '0.4rem',
  spacingS: '0.8rem',
  spacingM: '1.6rem',
  spacingL: '2.4rem',
  spacingXl: '3.2rem',
  spacingXxl: '4.2rem',

  /* --- GRID DEFAULTS --- */
  standardColumnWidth: `${standardColumnWidth}rem`,
  standardColumnGap: `${standardColumnGap}rem`,
  standardContentPadding: `${standardColumnWidth + standardColumnGap}rem`,
  // Mobile grid based on 4 Columns at 80px, 3 gaps plus 2 outer padding at 16px, total width 400px
  mobileColumnWidth: `${mobileColumnWidth}rem`,
  mobileColumnGap: `${mobileColumnGap}rem`,
  mobileDesignWidth: `${mobileDesignWidth}rem`,

  mobileImagePercentageWidth: `${
    (mobileImageWidth / mobileImageContentWrapperWidth) * 100
  }%`,
  standardDoubleImagePercentageWidth: `${
    (standardDoubleImageWidth / twelveColumnGridWidth) * 100
  }%`,
  doubleImageContentContainerPercentageWidth: `${
    (mobileImageContentWrapperWidth / mobileGridWidth) * 100
  }%`,

  /* --- BREAKPOINTS --- */
  // Note media queries are based on default font-size not declared font-size, so we can't use rems!
  breakPointMinWidth, // 320px
  breakPointTenColumn, // 480px
  breakPointSmallTablet, // 650px
  breakPointTablet, // 768px
  breakPointSmallDesktop, //1000px
  breakPointTwelveColumn, // 1064px
  breakPointFullWidth, // 1272px

  /* --- CONTENT WIDTHS --- */
  mainMaxWidth: '130rem',
  contentMinWidth: '32rem',
  contentMaxWidth: '130rem',
  contentNarrowWidth: `${contentNarrowWidth}rem`,
  contentContained: '101rem',

  /* --- RESPONSIVENESS PADDING/GAP %'s --- */
  mainContentContainerPaddingMobile: `${
    (mobileColumnGap / mobileDesignWidth) * 100
  }%`,
  mainContentContainerPaddingTenColumn: `${
    (standardColumnGap / tenColumnDesignWidth) * 100
  }%`,
  mobileColumnPercentageGap: `${(mobileColumnGap / mobileGridWidth) * 100}%`,
  standardColumnPercentageGap: `${
    (standardColumnGap / twelveColumnGridWidth) * 100
  }%`,
  standardColumnNarrowContentPercentageWidth: `${
    (standardColumnWidth / contentNarrowWidth) * 100
  }%`,
  standardGapNarrowContentPercentageWidth: `${
    (standardColumnGap / contentNarrowWidth) * 100
  }%`,
  tilePercentageWidth: '48.8',

  /* --- IMAGE DIMENSIONS --- */
  defaultMaxHeight: '60em', //600px on a 10px base

  /* --- BORDER DIMENSIONS --- */
  titleHighlightLineWidth: '3px',

  /* --- LOGO CARD VARIABLES --- */
  logoCardSize: '19.5rem',
  logoCardBackBorder: '6px',
  breakPointThreeLogos: '600px',

  /* --- DEFAULT IMAGES --- */
  default10ColPlaceholder: `${DEFAULT_ASSETS_BASE_PATH}4MZGlIpa9PKfU4cP2qS9ln/2fd88d2a2994779991eac86b52210e5b/emf-placeholder-image-10-col.png`,
  defaultFlipCardPlaceholder: `${DEFAULT_ASSETS_BASE_PATH}4Rp0p4wdkpktHykQtwSB2K/87f3847585a1165026bfa6991c07ed99/emf-placeholder-image-flipcard.png`,
  defaultOgImage: `${DEFAULT_ASSETS_BASE_PATH}RBkiQGtmJUuHsxWnq1bAc/68010684cfb6810d7ca106a570e1d2f9/og-card-default.png`,
  defaultTwImage: `${DEFAULT_ASSETS_BASE_PATH}2sL1NRjIdCzmKKdQCrfOEq/4001753d86c8499ec10a63d2da5ee5c4/twitter-card-default.png`,

  /* --- HOVER / TRANSITIONS --- */
  defaultTransitionDuration: `0.3s`,
  defaultTransition: `0.3s ease-in-out`,
  tileHoverBorder: '1px solid #2f2f46',
  imageHoverScale: 'scale(1.05)',
  linkFadeOpacity: '0.6',

  /* --- INTERACTIVE IMAGE BLOCK --- */
  tooltipZindex: 250,

  /* --- TILES --- */
  standardTileProps: {
    width: standardTileWidth,
    height: standardTileHeight,
    imageHeight: standardTileImageHeight,
    imageHeightPixel: 133,
    imageWidthPixel: 236,
    characterLimit: standardTileCharacterLimit,
    titleCharacterLimit: standardTileTitleCharacterLimit,
  },
  tacticalTileProps: {
    width: '100%',
    height: '36rem',
    imageHeight: '22.3rem',
    imageHeightPixel: 133,
    imageWidthPixel: 236,
    characterLimit: standardTileCharacterLimit,
  },
  listingTileProps: {
    width: standardTileWidth,
    height: standardTileHeight,
    imageHeight: standardTileImageHeight,
    imageHeightPixel: 203, // Larger image needed for listing tiles on tablet
    imageWidthPixel: 360, // Larger image needed for listing tiles on tablet
    characterLimit: standardTileCharacterLimit,
    mobileWidth: '100%',
    mobileHeight: '14.4rem',
    mobileImageHeight: '100%',
    mobileImageWidth: '14.4rem',
  },
  doubleTileProps: {
    width: '49.6rem',
    height: '41rem',
    imageHeight: '28.2rem',
    imageHeightPixel: 282,
    imageWidthPixel: 496,
    characterLimit: 250,
  },
  homepageDoubleTileProps: {
    width: '57.4rem',
    height: '42.2rem',
    imageHeight: '28.2rem',
    imageHeightPixel: 282,
    imageWidthPixel: 574,
    characterLimit: 250,
  },
  homepageStandardTileProps: {
    width: '27.45rem',
    height: '29.6rem',
    imageHeight: '15.8rem',
    imageHeightPixel: 316,
    imageWidthPixel: 276,
    characterLimit: 100,
  },

  /* --- EMBED BLOCK --- */
  iFrameDefaultHeight: '30rem',

  /* --- DESIGN SYSTEM --- */
  designSystemMenuWidthExpanded: '30rem',
  designSystemMenuWidthCollapsed: '5rem',
  designSystemLightGrey: '#f8f8f8',
  designSystemPurple: '#663399',
  designSystemPurpleLight: '#be9ede',

  /* PRIMARY NAV */
  primaryNavHeight: '5.6rem',
  topicsMarginTwelveColumn: '23rem',
  resourcesMarginTwelveColum: '6.5rem',
  topicsMarginTenColumn: '20rem',
  resourcesMarginTenColum: '4.5rem',

  /* MOBILE NAV */
  mobileNavBarHeight: '5.6rem',
  mobileNavBurgerRadius: '9.4rem',
  mobileNavSearchRadius: '6.5rem',
  mobileNavMenuZindex: '2000', // over site content
  mobileNavFlyoverZindex: '2050', // over content and main menu
  mobileNavBarZindex: '2100', // over everything
  mobileNavFadeColour: 'rgb(47, 47, 70, 0.15)',
  mobileNavCloseAtomWidth: '40px',

  /* LANGUAGE BAR */
  languageBarHeight: '3.2rem',

  /* SCROLL BARS */
  scrollbarSize: '3px', // Search "::-webkit-scrollbar" for full scrollbar styling
  scrollbarSize_beta: '6px', // BETA only ⚠️ to make scrolling easier on non-touch devices

  /* MASTHEAD */
  mastheadLargeHeight,
  mastheadIndentHeight,
  mastheadMobileIndentHeight,
  mastheadHeight: {
    pageTypeLinkAndIndent: `height: 34.3rem;
    @media screen and (min-width: ${breakPointTablet}) {
      height: ${mastheadLargeHeight};
    }`,
    indentOnly: `height: 26rem;
    @media screen and (min-width: ${breakPointTablet}) {
      height: ${mastheadLargeHeight};
    }`,
    pageTypeLinkOnly: `height: 26rem;
    @media screen and (min-width: ${breakPointTablet}) {
      height: ${mastheadLargeHeight};
    }`,
    titleOnly: `height: 24rem;
    @media screen and (min-width: ${breakPointTablet}) {
      height: calc(${mastheadLargeHeight} - ${mastheadIndentHeight});
    }`,
  },
  mastheadPaddingBottom: {
    pageTypeLinkAndIndent: `padding-bottom: 6rem;
    @media screen and (min-width: ${breakPointTablet}) {
      padding-bottom: 9rem;
    }`,
    indentOnly: `padding-bottom: ${mastheadMobileIndentHeight};
    @media screen and (min-width: ${breakPointTablet}) {
      padding-bottom: ${mastheadIndentHeight};
    }`,
    pageTypeLinkOnly: `padding-bottom: 2rem;
    @media screen and (min-width: ${breakPointTablet}) {
      padding-bottom: 6rem;
    }`,
    titleOnly: `padding-bottom: 0;`,
  },

  /* IMAGE SOURCE PARAMETERS */
  defaultImageFormat: 'png' /* &fm= */,
  defaultImageFit: 'fill' /* &fit= */,
  defaultImageQuality: 100 /* &q= */,
  defaultImageFocus: 'center' /* &f= */,
  /* Also include &w={imageWidth * 2} &h={imageHeight * 2} - doubled for retina */
};

export default vars;

//@TODO This could be expanded to export a selection of themes...
